import React from 'react'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  Plugin,
  ChartOptions,
  ChartData
} from 'chart.js'
import { Chart, } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'
import 'chartjs-adapter-moment'
import { useGlobalState } from 'state'
import { ObservatoryValue, ObservatoryRow } from 'common/types'
import { isPrivateMode, StageForecastHours } from 'common/consts'

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  annotationPlugin
)

type PumpChartProps = {
  observatory: ObservatoryRow
  height: number
  timeSeries: ObservatoryValue[]
}

export const PumpChart = ({observatory, height, timeSeries}: PumpChartProps) => {
  const state = useGlobalState()
  const [chartData, setChartData] = React.useState<ChartData>({labels: [], datasets: []})
  const [chartOptions, setChartOptions] = React.useState<ChartOptions>({})
  const [plugins, setPlugins] = React.useState<Plugin[]>([])
  const chartRef = React.useRef<ChartJS>(null)

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      chartRef.current?.update()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  React.useEffect(() => {
    loadTimeSeries()
  }, [timeSeries])

  React.useEffect(() => {
    setPlugins(getPlugins())
  }, [observatory])

  const getMaxLevel = (): number => {
    let maxLevel = 0
    if (observatory.landform !== undefined && observatory.landform.length > 0) {
      const yArr = observatory.landform.map(lf => lf[1])
      const landformMaxLevel = Math.max(...yArr)
      if (maxLevel < landformMaxLevel) {
        maxLevel = landformMaxLevel
      }
    }
    const levels = timeSeries.map(ts => ts.level).filter(level => level !== undefined) as number[]
    const tsMaxLevel = Math.max(...levels)
    if (maxLevel < tsMaxLevel) {
      maxLevel = tsMaxLevel
    }
    maxLevel += 1
    return Math.ceil(maxLevel + 1)
  }

  const getMinLevel = (): number => {
    let minLevel = 0
    if (observatory.landform !== undefined && observatory.landform.length > 0) {
      const yArr = observatory.landform.map(lf => lf[1])
      minLevel = Math.min(...yArr)
    } else {
      const levels = timeSeries.map(ts => ts.level).filter(level => level !== undefined) as number[]
      minLevel = Math.min(...levels)
    }
    return Math.floor(minLevel - 1)
  }

  const getMinDate = React.useCallback((): string => {
    return state.app.baseDate.subtract(3, 'hours').local().format('YYYY/MM/DD HH:mm')
  }, [state.app.baseDate])

  const getMaxDate = React.useCallback((): string => {
    const hours = isPrivateMode ? StageForecastHours : 0
    return state.app.baseDate.add(hours, 'hours').local().format('YYYY/MM/DD HH:mm')
  }, [state.app.baseDate])

  const loadTimeSeries = () => {
    const labels: string[] = []
    const levels: (number | null)[] = []
    const reals: (number | null)[] = []
    const forecasts: (number | null)[] = []

    timeSeries.forEach(ts => {
      labels.push(ts.date)
      const level = ts.level === undefined ? null : Number(ts.level.toFixed(2))
      levels.push(level)
      if (ts.type === 'real') {
        reals.push(level)
        forecasts.push(null)
      } else {
        reals.push(null)
        forecasts.push(level)
      }
    })

    const maxLevel = getMaxLevel()
    const minLevel = getMinLevel()

    const arr = timeSeries.filter(ts => ts.type === 'real' && ts.level !== undefined)
    const realLatestLevel = arr.length > 0 ? arr.slice(-1)[0].level : null

    setChartOptions({
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        xDate: {
          type: 'time',
          time: {
            tooltipFormat: 'MM/DD HH:mm',
            parser: 'YYYY/MM/DD HH:mm',
            unit: 'hour',
            minUnit: 'minute',
            displayFormats: {
              'hour': 'HH:mm',
            }
          },
          min: getMinDate(),
          max: getMaxDate()
        },
        yLevels: {
          ticks: {
            stepSize: 1
          },
          max: maxLevel,
          min: minLevel
        }
      },
      plugins: {
        filler: {
          drawTime: 'beforeDatasetsDraw'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y + 'm'
              }
              return label
            }
          }
        },
        annotation: {
          annotations: {
            nowLine: {
              borderColor: '#FF0000',
              borderWidth: 1.5,
              type: 'line',
              scaleID: 'xDate',
              value: state.app.baseDate.local().format('YYYY/MM/DD HH:mm'),
              label: {
                content: '現在',
                position: 'start',
                display: true
              }
            },
            waterBox: {
              type: 'box',
              drawTime: 'beforeDatasetsDraw',
              yScaleID: 'yLevels',
              borderColor: 'rgba(0,0,0,0)',
              yMin: realLatestLevel !== null ? realLatestLevel : minLevel,
              yMax: minLevel,
              backgroundColor: '#3D8AFF'
            },
          }
        }
      }
    })

    setChartData({
      labels,
      datasets: [
        {
          type: 'line' as const,
          label: '水位',
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yLevels',
          borderWidth: 2,
          borderColor: '#01163F',
          backgroundColor: '#01163F',
          pointBorderColor: '#01163F',
          pointRadius: 2,
          pointHitRadius: 0,
          data: reals,
          spanGaps: true
        },
        {
          type: 'line' as const,
          label: '水位',
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yLevels',
          borderWidth: 3,
          borderColor: '#3734dc',
          backgroundColor: '#3734dc',
          pointBorderColor: '#3734dc',
          pointRadius: 0,
          pointHitRadius: 0,
          borderDash: [10, 5],
          data: forecasts,
          spanGaps: true
        }
      ]
    })
  }

  const getPlugins = (): Plugin[] => {
    const landform = observatory.landform === undefined ? [] : observatory.landform
    if (landform.length <= 0) {
      return []
    }
    return [{
      id: 'landform',
      beforeDatasetsDraw: chart => {
        if (!chart.scales.xDate || !chart.scales.yLevels) {
          return
        }
        const ctx = chart.ctx
        if (ctx === null) return
        const xAxe = chart.scales.xDate
        const yAxe = chart.scales.yLevels
        const minGround = Math.min(...landform.map(row => row[0]))
        const maxGround = Math.max(...landform.map(row => row[0]))
        ctx.strokeStyle = 'red'
        ctx.beginPath()
        ctx.moveTo(xAxe.left, yAxe.bottom)
        for (let i = 0; i < landform.length; i++) {
          let x = landform[i][0] - minGround
          x = (x / (maxGround - minGround)) * xAxe.width + xAxe.left
          let y = yAxe.max - yAxe.min - (landform[i][1] - yAxe.min)
          y = (y / (yAxe.max - yAxe.min)) * yAxe.height + yAxe.top
          ctx.lineTo(x, y)
        }
        ctx.lineTo(xAxe.right, yAxe.bottom)
        ctx.lineTo(xAxe.left, yAxe.bottom)
        ctx.closePath()
        ctx.fillStyle = '#f1eeea'
        ctx.fill()
      }
    }]
  }

  return <Chart ref={chartRef} type='bar' data={chartData} height={height} options={chartOptions} plugins={plugins} redraw />
}
